import { useState, useRef, useEffect } from "react";
import { AlertCircle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChatMessage as ChatMessageType, MessageRole } from "@/types/chat";
import { chatApi } from "@/lib/api/chat";
import {
  handleCitationClick,
  linkifyCitations,
  sourceAnchorId,
} from "@/lib/chat-citations";
import { tableComponents } from "@/lib/markdown-renderers";

interface ChatInterfaceProps {
  sessionId?: string;
  licitacionId?: string;
  documentIds?: string[];
  // Mejora "Asistente con ámbito": filtro de familia que el padre
  // mantiene en su estado (segmented control). Si undefined → busca
  // en todo (comportamiento previo).
  familia?: "bases" | "apoyo";
  // Override visual: ocultar la cabecera "Chat con IA" porque el padre
  // ya tiene su propia cabecera con selector de licitación y filtro.
  hideHeader?: boolean;
  // Render personalizado del empty state. Por defecto se usa el genérico.
  emptyState?: React.ReactNode;
  onSessionCreated?: (sessionId: string) => void;
  onMessageSent?: () => void;
}

export default function ChatInterface({
  sessionId: initialSessionId,
  licitacionId,
  documentIds,
  familia,
  hideHeader,
  emptyState,
  onSessionCreated,
  onMessageSent,
}: ChatInterfaceProps) {
  const [sessionId, setSessionId] = useState<string | undefined>(initialSessionId);
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Sincronizar sessionId cuando el prop cambia (seleccionar otra sesión)
  useEffect(() => {
    setSessionId(initialSessionId);
    if (!initialSessionId) {
      setMessages([]);
    }
  }, [initialSessionId]);

  // Cargar mensajes de la sesión existente
  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    }
  }, [sessionId]);

  const loadSession = async (id: string) => {
    try {
      const session = await chatApi.getSession(id);
      setMessages(session.messages);
    } catch (error) {
      console.error("Error al cargar sesión:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue("");
    setIsLoading(true);

    // Añadir mensaje del usuario inmediatamente
    const tempUserMessage: ChatMessageType = {
      id: `temp-${Date.now()}`,
      role: MessageRole.USER,
      content: userMessage,
      sources: [],
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, tempUserMessage]);

    setErrorMessage(null);

    try {
      const response = await chatApi.sendMessage({
        message: userMessage,
        session_id: sessionId,
        licitacion_id: licitacionId,
        document_ids: documentIds,
        familia,
      });

      // Actualizar sessionId si es una nueva sesión
      if (!sessionId && response.session_id) {
        setSessionId(response.session_id);
        onSessionCreated?.(response.session_id);
      }

      // Reemplazar mensaje temporal con el real y añadir respuesta
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== tempUserMessage.id);
        return [
          ...filtered,
          {
            ...tempUserMessage,
            id: `user-${Date.now()}`,
          },
          response.message,
        ];
      });

      // Notificar al padre que se envió un mensaje
      onMessageSent?.();
    } catch (error) {
      console.error("Error al enviar mensaje:", error);
      setErrorMessage(
        error instanceof Error ? error.message : "Error al enviar el mensaje. Inténtalo de nuevo."
      );
      // Remover mensaje temporal
      setMessages((prev) => prev.filter((m) => m.id !== tempUserMessage.id));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header (opcional — el padre puede ocultarlo si ya gestiona su propio
          chrome con selector de licitación y filtro de ámbito). */}
      {!hideHeader && (
        <div className="px-5 py-3 border-b border-line">
          <h2 className="font-heading font-semibold text-base text-ink">
            Asistente IA
          </h2>
          {licitacionId && (
            <p className="text-xs text-content-muted mt-0.5">
              Licitación: {licitacionId}
            </p>
          )}
        </div>
      )}

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-h-0">
        {messages.length === 0 && (emptyState ?? (
          <div className="text-center mt-12">
            <p className="font-heading font-semibold text-lg text-ink mb-1">
              ¡Hola! Soy tu asistente
            </p>
            <p className="text-sm text-content-muted">
              Pregúntame sobre las licitaciones o documentos disponibles.
            </p>
          </div>
        ))}

        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {isLoading && (
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand flex items-center justify-center">
              <span className="text-white text-xs font-heading font-semibold">IA</span>
            </div>
            <div className="bg-surface-100 rounded p-3">
              <div className="flex space-x-1.5">
                <div className="w-1.5 h-1.5 bg-content-muted rounded-full animate-bounce"></div>
                <div className="w-1.5 h-1.5 bg-content-muted rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                <div className="w-1.5 h-1.5 bg-content-muted rounded-full animate-bounce" style={{ animationDelay: "0.4s" }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="mx-5 mb-2 p-3 rounded border-l-4 border-danger bg-danger/8 flex items-center gap-2 text-sm text-danger" role="alert">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMessage}</span>
          <button
            onClick={() => setErrorMessage(null)}
            className="ml-auto text-danger hover:text-danger/70"
            aria-label="Cerrar error"
          >
            &times;
          </button>
        </div>
      )}

      {/* Input */}
      <div className="px-5 py-3 border-t border-line">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Escribe tu pregunta…"
            className="flex-1 px-3 py-2 rounded border border-line bg-surface text-sm text-content placeholder:text-content-muted focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/15"
            disabled={isLoading}
            aria-label="Escribe tu pregunta al asistente"
          />
          <button
            type="submit"
            disabled={isLoading || !inputValue.trim()}
            className="px-5 py-2 rounded bg-brand text-white border border-brand-border font-heading font-semibold text-sm hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Enviar mensaje"
          >
            Enviar
          </button>
        </form>
      </div>
    </div>
  );
}

// Mejora "Citas legibles" del changelog cliente: las helpers
// `linkifyCitations` y `handleCitationClick` viven en
// `lib/chat-citations` y se comparten con el chat contextual de
// licitación para no duplicar la lógica.

function ChatMessage({ message }: { message: ChatMessageType }) {
  const isUser = message.role === MessageRole.USER;
  const processedContent = isUser
    ? message.content
    : linkifyCitations(message.content, message.id);

  return (
    <div className={`flex items-start space-x-3 ${isUser ? "justify-end" : ""}`}>
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand flex items-center justify-center">
          <span className="text-white text-xs font-heading font-semibold">IA</span>
        </div>
      )}

      <div className={`flex-1 max-w-3xl ${isUser ? "flex justify-end" : ""}`}>
        <div
          className={`rounded p-3 ${
            isUser
              ? "bg-brand text-white"
              : "bg-surface-100 text-content border border-line"
          }`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap text-sm">{message.content}</p>
          ) : (
            <div className="prose prose-sm max-w-none prose-headings:mt-3 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-li:my-0 prose-headings:font-heading prose-headings:text-ink prose-strong:text-ink">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  ...tableComponents,
                  // Chip clickeable para `[Fuente N]` → scrollea al detalle.
                  a: ({ href, children, ...rest }) => {
                    if (href?.startsWith("#source-")) {
                      return (
                        <a
                          href={href}
                          onClick={handleCitationClick}
                          className="inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded-pill text-[11px] font-heading font-semibold bg-brand/12 text-brand-hover hover:bg-brand hover:text-white no-underline transition-colors cursor-pointer align-middle"
                        >
                          {children}
                        </a>
                      );
                    }
                    return (
                      <a
                        href={href}
                        className="text-brand hover:text-brand-hover"
                        {...rest}
                      >
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {processedContent}
              </ReactMarkdown>
            </div>
          )}

          {/* Sources */}
          {!isUser && message.sources && message.sources.length > 0 && (
            <div className="mt-3 pt-3 border-t border-line">
              <p className="text-[11px] uppercase tracking-wider font-heading font-semibold text-content-muted mb-2">
                Fuentes
              </p>
              <ol className="space-y-2 list-none p-0 m-0">
                {message.sources.map((source, idx) => {
                  const sourceNum = idx + 1;
                  const anchorId = sourceAnchorId(sourceNum, message.id);
                  return (
                    <li
                      id={anchorId}
                      key={anchorId}
                      className="text-xs bg-surface p-2.5 rounded border border-line transition-shadow"
                    >
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-pill text-[10px] font-heading font-semibold bg-brand text-white flex-shrink-0">
                          {sourceNum}
                        </span>
                        <p className="font-heading font-semibold text-ink truncate">
                          {source.document_name}
                          {source.page && (
                            <span className="text-content-muted font-normal">
                              {" "}
                              · pág. {source.page}
                            </span>
                          )}
                        </p>
                      </div>
                      <p className="text-content-muted mt-1 line-clamp-3">
                        {source.content_preview}
                      </p>
                      <p className="text-content-muted/70 mt-1 text-[10px]">
                        Relevancia {Math.round(source.relevance * 100)}%
                      </p>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </div>
      </div>

      {isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-ink flex items-center justify-center">
          <span className="text-white text-xs font-heading font-semibold">TU</span>
        </div>
      )}
    </div>
  );
}
